class VehicleFinder extends HTMLElement {
  constructor() {
    super();

    this.items = [];
    // Reihenfolge der Produktgruppen in der Ergebnisliste
    this.typeOrder = ['Ladekantenschutz', 'Griffmulden-Schutzfolie', 'Display-Schutzfolie'];
    this.typeLabels = {
      Ladekantenschutz: 'Ladekantenschutz',
      'Griffmulden-Schutzfolie': 'Griffmulden-Schutzfolie',
      'Display-Schutzfolie': 'Display-Schutzfolie',
    };
    this.brands = [
      'Alfa Romeo', 'Aston Martin', 'Land Rover', 'Range Rover', 'Lynk & Co', 'Mercedes-Benz', 'Leap Motor',
      'Audi', 'BMW', 'BYD', 'Citroën', 'Citroen', 'Cupra', 'Dacia', 'DS', 'Fiat', 'Ford', 'Genesis', 'Honda',
      'Hyundai', 'Jaguar', 'Jeep', 'Kia', 'Leapmotor', 'Lexus', 'Mazda', 'Mercedes', 'MG', 'Mini', 'Mitsubishi',
      'Nissan', 'Opel', 'Peugeot', 'Polestar', 'Porsche', 'Renault', 'Seat', 'Smart', 'Subaru', 'Suzuki', 'Tesla',
      'Toyota', 'Volvo', 'VW', 'Volkswagen',
    ];
    this.brandAlias = {
      'Mercedes-Benz': 'Mercedes', Citroen: 'Citroën', Volkswagen: 'VW', 'Range Rover': 'Land Rover',
      'Leap Motor': 'Leapmotor',
    };

    this.form = this.querySelector('[data-vehicle-finder-form]');
    this.brandSelect = this.querySelector('[data-vehicle-finder-brand]');
    this.modelSelect = this.querySelector('[data-vehicle-finder-model]');
    this.variantSelect = this.querySelector('[data-vehicle-finder-variant]');
    this.submitButton = this.querySelector('[data-vehicle-finder-submit]');
    this.emptyState = this.querySelector('[data-vehicle-finder-empty]');
    this.results = this.querySelector('[data-vehicle-finder-results]');
    // Startseite: nach dem Absenden auf die Ergebnisseite wechseln; Ergebnisseite: Produktkarten zeigen
    this.resultsUrl = this.dataset.resultsUrl || '';
    this.gridMode = this.hasAttribute('data-grid');
    this.colors = ['Transparent', 'Schwarz Matt', 'Carbon'];
    // Rabatt ab 2 Artikeln in Prozent (Sektionseinstellung); 0 = keine Rabattanzeige
    this.bundleDiscount = Number(this.dataset.bundleDiscount || 0);

    this.placeholders = {
      brand: this.brandSelect.options[0] ? this.brandSelect.options[0].textContent : '',
      model: this.modelSelect.options[0] ? this.modelSelect.options[0].textContent : '',
      variant: this.variantSelect && this.variantSelect.options[0] ? this.variantSelect.options[0].textContent : '',
    };

    this.brandSelect.addEventListener('change', () => this.onBrandChange());
    this.modelSelect.addEventListener('change', () => this.onModelChange());
    if (this.variantSelect) this.variantSelect.addEventListener('change', () => this.updateSubmitState());
    this.form.addEventListener('submit', (event) => this.onSubmit(event));

    const loadingText = this.dataset.loadingText;
    if (loadingText) {
      this.brandSelect.disabled = true;
      this.brandSelect.innerHTML = `<option value="">${loadingText}</option>`;
    }

    this.loadItems();
  }

  // "Ladekantenschutz passend für VW Golf 8 Variant ab 2020 Carbon" -> {brand:'VW', model:'Golf', rest:'Golf 8 Variant ab 2020 Carbon'}
  parseFitment(title) {
    const match = title.match(/\bfür\s+(.+)$/i);
    if (!match) return null;
    const fitment = match[1].trim();

    const brand = this.brands
      .slice()
      .sort((a, b) => b.length - a.length)
      .find((b) => new RegExp(`^${b.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?=\\b|\\d)`, 'i').test(fitment));
    if (!brand) return null;

    let rest = fitment.slice(brand.length).trim();
    let name = this.brandAlias[brand] || brand;
    // Untermarken im Titel: "BMW MINI Clubman", "Citroën DS 7", "Mercedes-AMG SL"
    if (name === 'BMW' && /^mini\b/i.test(rest)) {
      name = 'Mini';
      rest = rest.replace(/^mini\s*/i, '');
    } else if (name === 'Citroën' && /^DS\s?\d/i.test(rest)) {
      name = 'DS';
      rest = rest.replace(/^DS\s*/i, '');
    } else if (name === 'Mercedes') {
      rest = rest.replace(/^-?AMG\s+/i, '');
    }
    const model = this.normalizeModel(name, rest);
    if (!model) return null;

    return { brand: name, model, rest };
  }

  // Modellname für die Auswahlliste; mehrteilige Namen zusammenhalten ("RS Q3", "e-tron GT", "ID.4", "Model Y")
  normalizeModel(brand, rest) {
    const rules = {
      Audi: [
        [/^RS\s?Q(\d)/i, (m) => `RS Q${m[1]}`],
        [/^RS\s?(\d)/i, (m) => `RS${m[1]}`],
        [/^e-?tron\s+GT\b/i, () => 'e-tron GT'],
        [/^(?:e-?tron\s+Q8|Q8\s+e-?tron)\b/i, () => 'Q8 e-tron'],
        [/^e-?tron\b/i, () => 'e-tron'],
      ],
      BMW: [[/^(\d)(?:er)?\b/i, (m) => `${m[1]}er`]],
      DS: [[/^(?:DS\s?)?(\d)\b/i, (m) => `DS ${m[1]}`]],
      Fiat: [[/^Abarth\s+(\d+)/i, (m) => `Abarth ${m[1]}`]],
      Hyundai: [
        [/^i(\d+)/i, (m) => `i${m[1]}`],
        [/^IONIQ\s?(\d)/i, (m) => `IONIQ ${m[1]}`],
        [/^Santa\s+Fe\b/i, () => 'Santa Fe'],
      ],
      Kia: [[/^e-?Niro\b/i, () => 'Niro']],
      Mazda: [[/^(CX|MX)[\s-]?(\d+)/i, (m) => `${m[1].toUpperCase()}-${m[2]}`]],
      Mercedes: [[/^(?:Vito\s+V-Klasse|V-Klasse(?:\s+Vito)?|Vito)\b/i, () => 'V-Klasse / Vito']],
      MG: [[/^(\d)\b/, (m) => `MG${m[1]}`]],
      Mini: [[/^(?:Cooper\b|F5[56]\b)/i, () => 'Cooper']],
      Tesla: [[/^Model\s+(\w)/i, (m) => `Model ${m[1].toUpperCase()}`]],
      VW: [
        [/^ID\.?\s?Buzz\b/i, () => 'ID. Buzz'],
        [/^ID\.?\s?(\d)/i, (m) => `ID.${m[1]}`],
        [/^up!?/i, () => 'up!'],
      ],
    };
    for (const [re, fn] of rules[brand] || []) {
      const m = rest.match(re);
      if (m) return fn(m);
    }
    return (rest.split(/\s+/)[0] || '').replace(/[,;]$/, '');
  }

  // Fallback, solange die Produktart im Shop noch nicht gepflegt ist
  typeFromTitle(title) {
    const t = title.toLowerCase();
    if (t.startsWith('ladekantenschutz')) return 'Ladekantenschutz';
    if (t.includes('türgriff') || t.includes('griffmulde')) return 'Griffmulden-Schutzfolie';
    if (t.startsWith('displayschutzfolie') || t.startsWith('display-schutzfolie')) return 'Display-Schutzfolie';
    return null;
  }

  async loadItems() {
    const base = this.dataset.collectionUrl || '';
    const items = [];

    try {
      // Schlanker Index (templates/collection.vf-index.liquid): Seite 1 nennt die Seitenzahl, Rest parallel
      const indexUrl = (page) => `${base}/collections/all?view=vf-index&page=${page}`;
      const first = await this.fetchJson(indexUrl(1));
      const rest = [];
      for (let page = 2; page <= (first.pages || 1); page += 1) rest.push(this.fetchJson(indexUrl(page)));
      [first, ...(await Promise.all(rest))].forEach((data) => this.addProducts(data.products || [], items));
    } catch (error) {
      // Index nicht erreichbar: wie früher über /products.json (groß, aber vollständig)
      items.length = 0;
      let page = 1;
      try {
        // eslint-disable-next-line no-constant-condition
        while (true) {
          const data = await this.fetchJson(`${base}/products.json?limit=250&page=${page}`);
          const products = data.products || [];
          this.addProducts(products, items);
          if (products.length < 250) break;
          page += 1;
        }
      } catch (fallbackError) {
        // Netzwerkfehler: mit dem arbeiten, was schon geladen ist.
      }
    }

    this.items = items;
    this.renderBrandOptions();
    if (this.gridMode) this.applyUrlParams();
  }

  async fetchJson(url) {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`${response.status} ${url}`);
    return JSON.parse(await response.text());
  }

  addProducts(products, items) {
    products.forEach((product) => {
      const type = this.typeOrder.includes(product.product_type)
        ? product.product_type
        : this.typeFromTitle(product.title);
      if (!type) return;
      const parsed = this.parseFitment(product.title);
      if (!parsed) return;
      const variant = (product.variants || [])[0] || {};
      const image = (product.images || [])[0];
      items.push({
        ...parsed,
        type,
        title: product.title,
        url: `/products/${product.handle}`,
        price: variant.price,
        variantId: variant.id,
        available: variant.available !== false,
        image: image ? image.src : null,
      });
    });
  }

  uniqueValues(items, key) {
    const seen = new Map();
    items.forEach((item) => {
      const k = item[key].toLowerCase();
      if (!seen.has(k)) seen.set(k, item[key]);
    });
    return Array.from(seen.values()).sort((a, b) => a.localeCompare(b, 'de', { numeric: true }));
  }

  fillSelect(select, values, placeholder) {
    select.innerHTML = '';
    const placeholderOption = document.createElement('option');
    placeholderOption.value = '';
    placeholderOption.textContent = placeholder;
    select.appendChild(placeholderOption);

    // Werte als Text oder als { value, label }
    values.forEach((value) => {
      const option = document.createElement('option');
      option.value = typeof value === 'object' ? value.value : value;
      option.textContent = typeof value === 'object' ? value.label : value;
      select.appendChild(option);
    });

    select.disabled = values.length === 0;
  }

  renderBrandOptions() {
    this.fillSelect(this.brandSelect, this.uniqueValues(this.items, 'brand'), this.placeholders.brand);
    this.fillSelect(this.modelSelect, [], this.placeholders.model);
    this.resetVariants();
    this.updateSubmitState();
  }

  onBrandChange() {
    const brand = this.brandSelect.value;
    const models = brand ? this.uniqueValues(this.items.filter((item) => item.brand === brand), 'model') : [];
    this.fillSelect(this.modelSelect, models, this.placeholders.model);
    this.resetVariants();
    this.updateSubmitState();
  }

  modelItems() {
    const brand = this.brandSelect.value;
    const model = this.modelSelect.value.toLowerCase();
    return this.items.filter((item) => item.brand === brand && item.model.toLowerCase() === model);
  }

  resetVariants() {
    if (this.variantSelect) this.fillSelect(this.variantSelect, [], this.placeholders.variant);
  }

  // Dritte Stufe: Baureihe / Baujahr / Karosserie aus den Titeln (assets/vehicle-variant.js)
  onModelChange() {
    if (this.variantSelect && window.VehicleVariant && this.modelSelect.value) {
      const options = window.VehicleVariant.buildOptions(this.modelItems());
      this.fillSelect(this.variantSelect, options, this.placeholders.variant);
      if (options.length === 1) this.variantSelect.value = options[0].value;
    } else {
      this.resetVariants();
    }
    this.updateSubmitState();
  }

  updateSubmitState() {
    const needsVariant = this.variantSelect && !this.variantSelect.disabled;
    this.submitButton.disabled = !(
      this.brandSelect.value &&
      this.modelSelect.value &&
      (!needsVariant || this.variantSelect.value)
    );
    if (this.emptyState) this.emptyState.hidden = true;
  }

  escape(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  countLabel(n) {
    return n === 1 ? '1 passendes Produkt' : `${n} passende Produkte`;
  }

  formatPrice(price) {
    const value = Number(price);
    if (Number.isNaN(value)) return '';
    return value.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
  }

  renderResults(matches) {
    if (!this.results) return;
    const groups = this.typeOrder
      .map((type) => ({ type, items: matches.filter((item) => item.type === type) }))
      .filter((group) => group.items.length);

    const brand = this.brandSelect.value;
    const model = this.modelSelect.value;
    const variant = this.variantSelect && this.variantSelect.value
      ? ` ${this.variantSelect.options[this.variantSelect.selectedIndex].textContent}`
      : '';
    let html = `<div class="vf-results__head"><h3>${this.countLabel(groups.reduce((n, g) => n + g.items.length, 0))} für ${this.escape(brand)} ${this.escape(model)}${this.escape(variant)}</h3></div>`;

    groups.forEach((group) => {
      group.items.sort((a, b) => a.rest.localeCompare(b.rest, 'de', { numeric: true }));
      html += `<section class="vf-results__group"><h4>${this.escape(this.typeLabels[group.type] || group.type)} <span>${group.items.length}</span></h4><ul class="vf-results__list">`;
      group.items.forEach((item) => {
        const img = item.image
          ? `<img src="${item.image}${item.image.includes('?') ? '&' : '?'}width=160" alt="" loading="lazy" width="64" height="64">`
          : '';
        html += `<li><a href="${item.url}">${img}<span class="vf-results__name">${this.escape(brand)} ${this.escape(item.rest)}</span><span class="vf-results__price">${this.formatPrice(item.price)}</span></a></li>`;
      });
      html += '</ul></section>';
    });

    this.results.innerHTML = html;
    this.results.hidden = false;
    this.results.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  // Ergebnisseite: Auswahl aus ?marke=…&modell=…&baureihe=… wiederherstellen und Produkte zeigen
  applyUrlParams() {
    const params = new URLSearchParams(window.location.search);
    const pick = (select, wanted, byLabel) => {
      if (!wanted) return false;
      const option = Array.from(select.options).find(
        (o) => (byLabel ? o.textContent : o.value).trim().toLowerCase() === wanted.trim().toLowerCase()
      );
      if (!option) return false;
      select.value = option.value;
      return true;
    };
    if (!pick(this.brandSelect, params.get('marke'))) return;
    this.onBrandChange();
    if (!pick(this.modelSelect, params.get('modell'))) return;
    this.onModelChange();
    if (this.variantSelect && !this.variantSelect.disabled && !this.variantSelect.value) {
      if (!pick(this.variantSelect, params.get('baureihe'), true)) return;
    }
    this.updateSubmitState();
    this.search();
  }

  selectionParams() {
    const params = new URLSearchParams();
    params.set('marke', this.brandSelect.value);
    params.set('modell', this.modelSelect.value);
    if (this.variantSelect && this.variantSelect.value) {
      params.set('baureihe', this.variantSelect.options[this.variantSelect.selectedIndex].textContent.trim());
    }
    return params;
  }

  colorPattern(color, flags) {
    return new RegExp(`\\b${color.replace(' ', '\\s*')}\\b`, flags);
  }

  colorOf(title) {
    return this.colors.find((c) => this.colorPattern(c, 'i').test(title)) || null;
  }

  // Name ohne Farbe und Füllwörter: "Audi A6 C7 2011-2018 Avant Allroad"
  cardName(item) {
    let name = `${this.brandSelect.value} ${item.rest}`;
    this.colors.forEach((c) => {
      name = name.replace(this.colorPattern(c, 'gi'), ' ');
    });
    return name
      .replace(/\b(Lackschutz\w*|Schutzfolie|Folie|Auto|Stoßstange|Griffmulden)\b/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  // Farbvarianten desselben Artikels zu einer Karte zusammenfassen
  buildCards(items) {
    const cards = new Map();
    items.forEach((item) => {
      const name = this.cardName(item);
      // Wortreihenfolge und Kurzschreibweisen egal: "C8 Avant ab 2018" = "C8 ab 2018 Avant", "ab 22 Face" = "ab 2022 Facelift"
      const words = name
        .toLowerCase()
        .replace(/\bface\b/g, 'facelift')
        .replace(/\bab (\d{2})\b/g, 'ab 20$1')
        .replace(/[–-]/g, ' ')
        .split(/\s+/)
        .filter(Boolean)
        .sort()
        .join(' ');
      const key = `${item.type}|${words}`;
      if (!cards.has(key)) cards.set(key, { type: item.type, name, variants: [] });
      const card = cards.get(key);
      // ausführlichste Schreibweise als Kartenname ("ab 2022 Facelift" statt "ab 22 Face")
      if (name.length > card.name.length) card.name = name;
      card.variants.push({ ...item, color: this.colorOf(item.title) });
    });
    return Array.from(cards.values()).map((card) => {
      card.variants.sort((a, b) => this.colors.indexOf(a.color) - this.colors.indexOf(b.color));
      card.selected = 0;
      return card;
    });
  }

  imageUrl(src, width) {
    if (!src) return '';
    return `${src}${src.includes('?') ? '&' : '?'}width=${width}`;
  }

  cardHtml(card, index) {
    const v = card.variants[card.selected];
    const swatches = v.color
      ? `<div class="vf-card__colors" role="group" aria-label="Farbe">${card.variants
          .map(
            (variant, i) =>
              `<button type="button" class="vf-card__color${i === card.selected ? ' is-active' : ''}" data-card="${index}" data-variant="${i}" aria-pressed="${i === card.selected}"><span class="vf-swatch vf-swatch--${(variant.color || 'standard').toLowerCase().replace(/\s+/g, '-')}"></span>${this.escape(variant.color || 'Standard')}</button>`
          )
          .join('')}</div>`
      : '';
    return `<article class="vf-card">
      <a class="vf-card__media" href="${v.url}">${v.image ? `<img src="${this.imageUrl(v.image, 600)}" alt="${this.escape(card.name)}" loading="lazy" width="300" height="300">` : ''}</a>
      <div class="vf-card__body">
        <p class="vf-card__type">${this.escape(this.typeLabels[card.type] || card.type)}</p>
        <h3 class="vf-card__title"><a href="${v.url}">${this.escape(card.name)}</a></h3>
        ${swatches}
        <p class="vf-card__price">${this.formatPrice(v.price)}</p>
        <div class="vf-card__actions">
          <button type="button" class="button vf-card__add" data-add="${v.variantId}"${v.available ? '' : ' disabled'}>${v.available ? 'In den Warenkorb' : 'Ausverkauft'}</button>
          <a class="vf-card__more" href="${v.url}">Details</a>
        </div>
      </div>
    </article>`;
  }

  // Komplett-Schutz: je Produktgruppe der erste Artikel in der gewählten Farbe
  bundleHtml() {
    const picks = this.typeOrder
      .map((type) => this.cards.find((card) => card.type === type))
      .filter(Boolean)
      .map((card) => card.variants[card.selected])
      .filter((v) => v.available);
    if (picks.length < 2) return '';
    const total = picks.reduce((sum, v) => sum + Number(v.price || 0), 0);
    const rows = picks
      .map(
        (v) =>
          `<li><span>${this.escape(this.typeLabels[v.type] || v.type)}${v.color ? ` · ${this.escape(v.color)}` : ''}</span><span>${this.formatPrice(v.price)}</span></li>`
      )
      .join('');
    // Set-Vergleich mit den aktuellen Einzelpreisen (kein Streichpreis); der Rabatt ist ein automatischer Shopify-Rabatt ab 2 Artikeln
    const rabatt = this.bundleDiscount;
    const setPreis = Math.round(total * (100 - rabatt)) / 100;
    const preis = rabatt
      ? `<p class="vf-bundle__single">Einzeln ${this.formatPrice(total)}</p>
        <p class="vf-bundle__total"><span>im Set</span> ${this.formatPrice(setPreis)}</p>
        <p class="vf-bundle__save">Du sparst ${this.formatPrice(total - setPreis)} (${rabatt} %) – wird im Warenkorb automatisch abgezogen</p>`
      : `<p class="vf-bundle__total">${this.formatPrice(total)}</p>`;
    return `<div class="vf-bundle">
      <div class="vf-bundle__text">
        <p class="vf-bundle__eyebrow">Komplett-Schutz${rabatt ? ` · ${rabatt} % Set-Rabatt` : ''}</p>
        <h3>Alles für deinen ${this.escape(this.vehicleLabel())}</h3>
        <ul>${rows}</ul>
      </div>
      <div class="vf-bundle__buy">
        ${preis}
        <button type="button" class="button vf-bundle__add" data-add="${picks.map((v) => v.variantId).join(',')}">Alle ${picks.length} in den Warenkorb</button>
      </div>
    </div>`;
  }

  vehicleLabel() {
    const variant =
      this.variantSelect && this.variantSelect.value
        ? ` ${this.variantSelect.options[this.variantSelect.selectedIndex].textContent}`
        : '';
    return `${this.brandSelect.value} ${this.modelSelect.value}${variant}`;
  }

  renderGrid(matches) {
    if (!this.results) return;
    this.cards = this.buildCards(matches);
    this.drawGrid();
    if (!this.gridListener) {
      this.gridListener = true;
      this.results.addEventListener('click', (event) => this.onGridClick(event));
    }
  }

  drawGrid() {
    const groups = this.typeOrder
      .map((type) => ({
        type,
        cards: this.cards.map((card, i) => ({ card, i })).filter((c) => c.card.type === type),
      }))
      .filter((group) => group.cards.length);
    let html = `<div class="vf-grid__head"><h2>${this.countLabel(this.cards.length)} für ${this.escape(this.vehicleLabel())}</h2></div>`;
    html += this.bundleHtml();
    // ein gemeinsames Raster, nach Produktgruppe sortiert; die Gruppe steht auf der Karte
    html += `<div class="vf-grid">${groups
      .flatMap((group) => group.cards)
      .map((c) => this.cardHtml(c.card, c.i))
      .join('')}</div>`;
    this.results.innerHTML = html;
    this.results.hidden = false;
  }

  onGridClick(event) {
    const color = event.target.closest('[data-card][data-variant]');
    if (color) {
      this.cards[Number(color.dataset.card)].selected = Number(color.dataset.variant);
      this.drawGrid();
      return;
    }
    const add = event.target.closest('[data-add]');
    if (add) this.addToCart(add, add.dataset.add.split(','));
  }

  async addToCart(button, ids) {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    button.disabled = true;
    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: ids.map((id) => ({ id: Number(id), quantity: 1 })) }),
      });
      if (!response.ok) throw new Error('cart');
      window.location.href = `${root}cart`;
    } catch (error) {
      button.disabled = false;
      button.textContent = 'Fehler – bitte erneut versuchen';
    }
  }

  onSubmit(event) {
    event.preventDefault();
    if (this.resultsUrl) {
      window.location.href = `${this.resultsUrl}?${this.selectionParams().toString()}`;
      return;
    }
    if (this.gridMode) {
      window.history.replaceState(null, '', `${window.location.pathname}?${this.selectionParams().toString()}`);
    }
    this.search();
  }

  search() {
    const variant = this.variantSelect ? this.variantSelect.value : '';
    const matches = this.modelItems().filter(
      (item) => !variant || !window.VehicleVariant || window.VehicleVariant.matches(item, variant)
    );

    if (matches.length) {
      if (this.gridMode) this.renderGrid(matches);
      else this.renderResults(matches);
    } else if (this.emptyState) {
      this.emptyState.hidden = false;
    }
  }
}

customElements.define('vehicle-finder', VehicleFinder);
